<template>
    <div v-if="isActive" class="modal-overlay" @click.self="close">
        <div class="modal-container">
            <div class="modal-header bg-primary text-white">
                <h5 class="modal-title">{{ modalTitle }}</h5>
                <span class="close" @click="close">&times;</span>
            </div>
            <div class="modal-body">
                <div v-if="!ticket.id" class="panel">
                    <div class="form-group">
                        <h5 class="form-label fw-bold">{{ settings?.title }}</h5>
                        <p v-html="settings?.content"></p>
                    </div>
                    <div class="row">
                        <div class="col-md-3">
                            <label>Submitter Name</label>
                            <input
                                type="text"
                                class="form-control"
                                v-model="ticket.submitter_name"
                                :readonly="isLoading"
                                :disabled="isFilledSubmitterName"
                            >
                            <div class="error-messages">{{ messageFieldError('submitter_name') }}</div>
                        </div>
                        <div class="col-md-3">
                            <label>Contact Email</label>
                            <input
                                type="email"
                                class="form-control"
                                v-model="ticket.submitter_email"
                                :readonly="isLoading"
                                :disabled="isFilledSubmitterEmail"
                            >
                            <div class="error-messages">{{ messageFieldError('submitter_email') }}</div>
                        </div>
                        <div class="col-md-3">
                            <label>Category</label>
                            <select class="form-select" v-model="ticket.category_id">
                                <option v-for="category in categories" :key="category.id" :value="category.id">
                                    {{ category.name }}
                                </option>
                            </select>
                            <div class="error-messages">{{ messageFieldError('category_id') }}</div>
                        </div>
                        <div class="col-md-3">
                            <label>Priority</label>
                            <select class="form-select" v-model="ticket.priority_id">
                                <option v-for="priority in priorities" :key="priority.id" :value="priority.id">
                                    {{ priority.name }}
                                </option>
                            </select>
                            <div class="error-messages">{{ messageFieldError('priority_id') }}</div>
                        </div>
                    </div>
                    <div class="form-group mt-3">
                        <label>Subject</label>
                        <input type="text" class="form-control" v-model="ticket.subject" :readonly="isLoading">
                        <div class="error-messages">{{ messageFieldError('subject') }}</div>
                    </div>

                    <div class="form-group mt-3">
                        <EditorComponent :value.sync="ticket.comment.value" :error-messages="messageFieldError('value')" height="200px"/>
                        <div class="error-messages">{{ messageFieldError('comment_value') }}</div>
                    </div>

                    <div class="file-uploader mt-4">
                        <label :for="ticket.comment.attachment" class="form-label"></label>
                        <div class="position-relative">
                            <input ref="fileInput" type="file" class="form-control pe-5" @change="handleFileUpload"/>
                            <span
                                v-if="ticket.comment.attachment"
                                @click="clearFile"
                                class="position-absolute file-clear top-50 end-0 translate-middle-y me-3"
                            >✕</span>
                        </div>
                        <div v-if="messageFieldError('attachment')" class="text-danger mt-1">
                            {{ messageFieldError('attachment') }}
                        </div>

                        <div
                            v-if="ticket.comment.attachment && ticket.comment.attachment.size > maxFileSize.max_file_size_b"
                            class="alert alert-warning mt-2"
                        >
                            You uploaded the file larger than {{ maxFileSize.max_file_size_mb }} MB.
                        </div>

                        <div class="alert alert-warning mt-2">
                            Accepted Formats: DOC(X), GIF, JPG, PDF, PNG, TXT, XLS(X);
                            {{ maxFileSize.warning }}
                        </div>
                    </div>
                </div>
                <div v-if="!ticket.id" class="modal-footer">
                    <button :disabled="isLoading" class="btn btn-primary mt-2 mx-2" style="width: 200px;" @click="submit">Submit</button>
                    <button class="btn btn-secondary mt-2 mx-2" style="width: 200px;" @click="close">Cancel</button>
                </div>

                <div v-if="ticket.id">
                    <div class="card">
                        <div class="card-body">
                            <div class="left-panel">
                                <p><strong>Contact Name:</strong> {{ ticket.submitter_name }}</p>
                                <p><strong>Contact Email:</strong> {{ ticket.submitter_email }}</p>
                                <p><strong>Subject:</strong> {{ ticket.subject }}</p>
                                <p v-if="ticket.category"><strong>Category:</strong> {{ ticket.category }}</p>
                                <p v-if="ticket.priority"><strong>Priority:</strong> {{ ticket.priority }}</p>
                                <p v-if="ticket.status"><strong>Status:</strong> {{ ticket.status }}</p>
                                <p v-if="ticket.assigned_to"><strong>Assigned To:</strong> {{ ticket.assigned_to }}</p>
                                <p v-if="ticket.due_date"><strong>Due Date:</strong> {{ ticket.due_date }}</p>
                            </div>

                            <div class="right-panel">
                                <div class="button-group">
                                    <button v-if="!add_public_comment" class="btn btn-primary mb-2" style="width: 200px;" @click="addPublicComment">
                                        Add reply message
                                    </button>
                                    <button v-if="add_public_comment" class="btn btn-primary mt-2" style="width: 200px;" @click="cancelPublicComment">
                                        Cancel reply message
                                    </button>
                                </div>

                                <div v-if="add_public_comment" class="comment-section">
                                    <h6>Reply Message</h6>
                                    <EditorComponent :value.sync="ticket.comment.value" :error-messages="messageFieldError('value')" height="200px"/>
                                    <div class="file-uploader mt-4">
                                        <label :for="ticket.comment.attachment" class="form-label"></label>
                                        <div class="position-relative">
                                            <input ref="fileInput" type="file" class="form-control pe-5" @change="handleFileUpload"/>
                                            <span
                                                v-if="ticket.comment.attachment"
                                                @click="clearFile"
                                                class="position-absolute file-clear top-50 end-0 translate-middle-y me-3"
                                            >✕</span>
                                        </div>

                                        <div v-if="messageFieldError('attachment')" class="text-danger mt-1">
                                            {{ messageFieldError('attachment') }}
                                        </div>

                                        <div
                                            v-if="ticket.comment.attachment && ticket.comment.attachment.size > maxFileSize.max_file_size_b"
                                            class="alert alert-warning mt-2"
                                        >
                                            You uploaded the file larger than {{ maxFileSize.max_file_size_mb }} MB.
                                        </div>

                                        <div class="alert alert-warning mt-2">
                                            Accepted Formats: DOC(X), GIF, JPG, PDF, PNG, TXT, XLS(X);
                                            {{ maxFileSize.warning }}
                                        </div>
                                    </div>

                                    <button class="btn btn-primary mt-2" :disabled="isLoading" style="width: 200px;" @click="submit">Submit</button>
                                </div>

                                <div v-for="comment in ticket.comments" :key="comment.id" class="comment-box">
                                    <div class="comment-header">
                                        <span class="comment-date">{{ comment.created_at }}</span>
                                        <span class="comment-author">{{ comment.author_name }}</span>
                                    </div>
                                    <div class="comment-body">
                                        <p v-html="comment.comment"></p>
                                    </div>

                                    <AttachmentsBlock :attachments="comment.attachments" />

                                    <div v-if="comment.read_at" class="comment-footer">
                                        Read at: {{ comment.read_at }}
                                    </div>
                                </div>

                                <div class="history-section">
                                    <h6 class="text-danger">History</h6>
                                    <p v-if="ticket.history && ticket.history.length > 0" v-html="ticket.history"></p>
                                    <p v-else>No history available</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EditorComponent from "./EditorComponent.vue";
import FileUploader from "../support-desk/components/FileUploader.vue";
import maxFileSizeMixin from "../mixins/files/maxFileSize";
import notifications from "../mixins/notifications";
import AttachmentsBlock from "./AttachmentsBlock.vue";

export default {
    name: "TicketComponent",
    components: {AttachmentsBlock, FileUploader, EditorComponent},
    mixins: [maxFileSizeMixin, notifications],
    props: {
        isActive: {
            type: Boolean,
            default: false
        },
        id: {
            type: Number,
            default: 0
        },
        url: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            default: 'Ticket'
        },
        settings: {
            type: Object,
            default: {}
        },
        categories: {
            type: Array,
            default: () => []
        },
        priorities: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            dueDateMenu: false,
            isLoading: false,
            isFilledSubmitterName: false,
            isFilledSubmitterEmail: false,
            ticket: {
                id: 0,
                submitter_name: '',
                submitter_email: '',
                category_id: null,
                priority_id: null,
                status_id: null,
                assigned_to_id: null,
                due_date: null,
                subject: '',
                history: '',
                comment: {
                    value: '',
                    visibility: 'public',
                    attachment: null
                },
                comments: [],
            },
            add_public_comment: false,
        }
    },
    computed: {
        modalTitle() {
            return (this.ticket?.id ? 'Edit' : 'New') + ' ' + this.title;
        }
    },
    watch: {
        '$props.isActive': function (value) {
            this.clearValidateErrors();
            this.loadAuth();
            if (value) {
                this.loadMaxFileSize('files');
                if (this.id > 0) {
                    this.loadData();
                }
            } else {
                this.clearData();
            }
        },
    },
    methods: {
        loadAuth() {
            this.isLoading = true;

            this.$api.get('api/v1/auth').then(response => {
                if (response.data?.is_auth) {
                    const { name, email } = response.data.user;

                    this.ticket.submitter_name = name;
                    this.ticket.submitter_email = email;

                    this.isFilledSubmitterName = Boolean(name);
                    this.isFilledSubmitterEmail = Boolean(email);
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadData() {
            this.isLoading = true;

            this.$api.get(this.url + '/' + this.id).then(response => {
                if (response.data) {
                    this.ticket = {
                        ...response.data,
                        comment: {
                            value: '',
                            visibility: 'public',
                            attachment: null
                        }
                    };
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        clearData() {
            this.ticket.id = 0;
            this.ticket.submitter_name = '';
            this.ticket.submitter_email = '';
            this.ticket.category_id = null;
            this.ticket.priority_id = null;
            this.ticket.subject = '';
            this.ticket.status_id = null;
            this.ticket.assigned_to_id = null;
            this.ticket.due_date = null;
            this.ticket.history = '';
            this.clearComment();
        },
        submit() {
            this.isLoading = true;

            if (this.ticket.id > 0) {
                this.submitComment();
            } else {
                this.createTicket();
            }
        },
        submitComment() {
            const formData = new FormData();

            for (const key in this.ticket.comment) {
                if (this.ticket.comment[key] !== null && this.ticket.comment[key] !== undefined) {
                    formData.append(key, this.ticket.comment[key]);
                }
            }

            this.$api.post(this.url + '/' + this.ticket.id + '/comments', formData,
                { headers: { 'Content-Type': 'multipart/form-data'}})
                .then(response => {
                    if (response.data) {
                        this.ticket = {
                            ...response.data,
                            comment: {
                                value: '',
                                visibility: 'public',
                                attachment: null
                            }
                        };
                        this.clearValidateErrors();
                        this.cancelPublicComment();
                    }
                }).catch((error) => {
                if (error.response && error.response.data.errors) {
                    this.setValidateErrors(error.response.data.errors);
                }
            }).finally(() => {
                this.isLoading = false;
                this.$emit('reload');
            });
        },
        createTicket() {
            const formData = new FormData();

            for (const key in this.ticket) {
                if (key === 'comment' && this.ticket.comment) {
                    if (this.ticket.comment.value) {
                        formData.append('value', this.ticket.comment.value);
                    }

                    if (this.ticket.comment.attachment instanceof File) {
                        formData.append('attachment', this.ticket.comment.attachment);
                    }
                } else {
                    if (this.ticket[key] !== null && this.ticket[key] !== undefined) {
                        formData.append(key, this.ticket[key]);
                    }
                }
            }

            this.$api.post(this.url, formData,
                { headers: { 'Content-Type': 'multipart/form-data'}})
                .then(response => {
                    if (response.data) {
                        this.clearValidateErrors();
                        this.close();
                    }
                }).catch((error) => {
                if (error.response && error.response.data.errors) {
                    this.setValidateErrors(error.response.data.errors);
                }
            }).finally(() => {
                this.isLoading = false;
                this.$emit('reload');
            });
        },
        close() {
            this.isLoading = false;
            this.$emit('update:isActive', false);
            this.cancelPublicComment();

            this.$nextTick(() => {
                this.itemEdit = { id: null, isActive: false };
            });
        },
        clearComment() {
            this.ticket.comment = {
                value: '',
                visibility: 'public',
                attachment: null
            };
        },
        addPublicComment() {
            this.add_public_comment = true;
            this.ticket.comment.visibility = 'public';
        },
        loadMaxFileSize(warning) {
            this.isLoading = true;

            this.$api.get('/api/v1/admin/files/max-size/' + warning).then(response => {
                if (response.data) {
                    this.maxFileSize = response.data;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        cancelPublicComment() {
            this.add_public_comment = false;
            this.clearComment();
        },
        formatDate(date, format = 'YYYY-MM-DD') {
            if (!date) return '';
            return new Date(date).toISOString().split('T')[0];
        },
        getFileName(url) {
            return url.split('/').pop();
        },
        handleFileUpload(event) {
            this.ticket.comment.attachment = event.target.files[0] || null;
        },
        clearFile() {
            this.ticket.comment.attachment = null;
            this.$refs.fileInput.value = null;
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3000;
    overflow: hidden;
}

.modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 0;
    max-height: calc(90vh - 120px);
}

.modal-container {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 5px;
    width: 90vw;
    height: 90vh;
    overflow: hidden;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    margin: auto;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-bottom: 1px solid #ddd;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    padding: 10px;
    border-top: 1px solid #ddd;
}
.card {
    padding: 0 !important;
    background: #fff;
}

.card-body {
    display: flex;
    flex-wrap: wrap;
    padding: 0 !important;
}

.panel {
    padding: 25px;
}

.left-panel {
    flex: 1;
    min-width: 250px;
    padding: 25px;
    border-right: 1px solid #ddd;
}

.right-panel {
    flex: 3;
    padding: 25px;
}

.form-group {
    margin-bottom: 15px;
}

.comment-section {
    margin-top: 20px;
}

.comment-box {
    border: 1px solid #ddd;
    border-radius: 5px;
    padding: 10px;
    margin-top: 10px;
    background: #f9f9f9;
}

.comment-header {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: gray;
}

.comment-body {
    margin-top: 5px;
}

.comment-footer {
    font-size: 12px;
    color: gray;
    margin-top: 5px;
}

.history-section {
    margin-top: 20px;
    padding: 10px;
    border-top: 1px solid #ddd;
}

.button-group {
    margin-bottom: 10px;
}

.btn {
    padding: 6px 12px;
    font-size: 14px;
    border-radius: 4px;
    cursor: pointer;
}

.btn-outline-primary {
    background: none;
    border: 1px solid #007bff;
    color: #007bff;
}

.btn-outline-secondary {
    background: none;
    border: 1px solid #6c757d;
    color: #6c757d;
}

.btn-primary {
    background: #007bff;
    color: white;
    border: none;
}

.alert {
    padding: 5px 10px;
    border-radius: 3px;
}

.alert-success {
    background: #d4edda;
    color: #155724;
}

.error-messages {
    color: #ff5252 !important;
    font-size: 12px;
}

.form-select {
    min-width: 250px;
    min-height: 35px;
    width: 100%;
}

.file-clear {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    z-index: 2000;
}

</style>
